import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import {
  Activity,
  BarChart2,
  Bot,
  Brain,
  Building2,
  Calendar,
  Database,
  FileSearch,
  FileText,
  Loader2,
  Phone,
  RefreshCw,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Stethoscope,
  User,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/_protected/admin/')({
  component: AdminHub,
});

// Define specific route paths for type safety
type AdminRoutePath =
  | '/admin/analytics'
  | '/admin/qa-export'
  | '/admin/manual-sync'
  | '/admin/schedule-visualizer'
  | '/admin/patient-management'
  | '/admin/appointment-types'
  | '/admin/provider-appointment-types'
  | '/admin/provider-rules'
  | '/admin/agent-facilities'
  | '/admin/provider-insurance'
  | '/admin/insurance-phrase-mappings'
  | '/admin/scheduling-rules'
  | '/admin/facilities'
  | '/admin/providers'
  | '/admin/restriction-configs'
  | '/admin/agent-transfer-destinations'
  | '/admin/retell-agent-sync';

interface AdminToolCard {
  title: string;
  description: string;
  icon: React.ElementType;
  path: AdminRoutePath;
  category: 'operations' | 'configuration' | 'review';
  keywords?: string[];
}

const adminTools: AdminToolCard[] = [
  // Daily Operations - Most frequently used
  {
    title: 'Analytics',
    description: 'Auto-resolution rate, failure distribution, and call volume reporting',
    icon: BarChart2,
    path: '/admin/analytics',
    category: 'operations',
    keywords: ['analytics', 'reporting', 'resolution', 'calls', 'stats'],
  },
  {
    title: 'QA Export',
    description: 'Export quality assurance and conversation reports',
    icon: FileText,
    path: '/admin/qa-export',
    category: 'operations',
    keywords: ['qa', 'export', 'report', 'quality', 'conversation', 'daily'],
  },
  {
    title: 'Manual Sync',
    description: 'Manually trigger data synchronization with EHR systems',
    icon: RefreshCw,
    path: '/admin/manual-sync',
    category: 'operations',
    keywords: ['sync', 'refresh', 'ehr', 'data', 'trigger'],
  },
  {
    title: 'Schedule Visualizer',
    description: 'Debug and view synced schedules from the database',
    icon: FileSearch,
    path: '/admin/schedule-visualizer',
    category: 'operations',
    keywords: ['debug', 'schedule', 'view', 'database', 'troubleshoot'],
  },
  {
    title: 'Patient Management',
    description: 'View and update patient demographics and insurance information',
    icon: User,
    path: '/admin/patient-management',
    category: 'operations',
    keywords: ['patient', 'demographics', 'insurance', 'update', 'edit', 'intake'],
  },
  {
    title: 'Retell Agent Sync',
    description: 'Copy agent configurations between dev and prod environments',
    icon: RefreshCw,
    path: '/admin/retell-agent-sync',
    category: 'operations',
    keywords: ['retell', 'agent', 'sync', 'copy', 'environment', 'dev', 'prod', 'configuration'],
  },
  {
    title: 'Agent Transfer Destinations',
    description: 'Configure per-agent call transfer overrides',
    icon: Phone,
    path: '/admin/agent-transfer-destinations',
    category: 'operations',
    keywords: ['agent', 'transfer', 'call', 'destination', 'override', 'sip', 'phone'],
  },

  // Configuration - Frequently changed settings
  {
    title: 'Appointment Types',
    description: 'Configure appointment types and duration settings',
    icon: Calendar,
    path: '/admin/appointment-types',
    category: 'configuration',
    keywords: ['appointment', 'types', 'schedule', 'booking', 'rules'],
  },
  {
    title: 'Provider Appointment Types',
    description: 'Map appointment types to specific providers',
    icon: User,
    path: '/admin/provider-appointment-types',
    category: 'configuration',
    keywords: ['provider', 'doctor', 'appointment', 'mapping', 'assign'],
  },
  {
    title: 'Provider Qualification Rules',
    description:
      'Configure which providers can see patients based on conditions, intents, and procedures',
    icon: ShieldCheck,
    path: '/admin/provider-rules',
    category: 'configuration',
    keywords: [
      'provider',
      'rules',
      'qualification',
      'eligibility',
      'allow',
      'deny',
      'tags',
      'conditions',
    ],
  },
  {
    title: 'Agent-Facility Management',
    description: 'Assign AI agents to medical facilities',
    icon: Bot,
    path: '/admin/agent-facilities',
    category: 'configuration',
    keywords: ['agent', 'ai', 'facility', 'assignment', 'bot', 'onboarding'],
  },
  {
    title: 'Provider Insurance',
    description: 'Configure insurance plans accepted by each provider',
    icon: ShieldCheck,
    path: '/admin/provider-insurance',
    category: 'configuration',
    keywords: ['insurance', 'provider', 'plan', 'coverage', 'accept'],
  },

  // Review & Maintenance - Less frequently used
  {
    title: 'Insurance Phrase Mappings',
    description: 'Review AI-generated insurance phrase mappings',
    icon: Brain,
    path: '/admin/insurance-phrase-mappings',
    category: 'review',
    keywords: ['insurance', 'phrase', 'mapping', 'semantic', 'ai', 'review'],
  },
  {
    title: 'Scheduling Rules',
    description: 'View and configure scheduling logic across providers',
    icon: Settings,
    path: '/admin/scheduling-rules',
    category: 'review',
    keywords: ['schedule', 'rule', 'logic', 'mental model', 'reference'],
  },
  {
    title: 'Facility Management',
    description: 'Manage facility addresses, privacy settings, and schedule sync',
    icon: Building2,
    path: '/admin/facilities',
    category: 'review',
    keywords: [
      'facility',
      'privacy',
      'surgery',
      'center',
      'location',
      'settings',
      'address',
      'sync',
    ],
  },
  {
    title: 'Provider Configuration',
    description: 'Review provider scheduling settings and age restrictions',
    icon: Stethoscope,
    path: '/admin/providers',
    category: 'review',
    keywords: [
      'provider',
      'doctor',
      'schedule',
      'age',
      'restrictions',
      'configuration',
      'can schedule',
    ],
  },
  {
    title: 'Restriction Configurations',
    description: 'Manage appointment slot restrictions and label mappings',
    icon: Database,
    path: '/admin/restriction-configs',
    category: 'review',
    keywords: ['restriction', 'slot', 'label', 'mapping', 'config', 'advanced'],
  },
];

const categoryInfo = {
  operations: {
    title: 'Daily Operations',
    description: 'Frequently used tools for daily tasks',
    icon: Activity,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  configuration: {
    title: 'Configuration',
    description: 'Manage system settings and assignments',
    icon: Settings,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
  review: {
    title: 'Review & Maintenance',
    description: 'Review settings and occasional maintenance',
    icon: FileSearch,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
};

function AdminHub() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // Filter tools based on search and category
  const filteredTools = useMemo(() => {
    return adminTools.filter((tool) => {
      const matchesSearch = searchQuery
        ? tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tool.keywords?.some((keyword) =>
            keyword.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : true;

      const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  // Group tools by category
  const groupedTools = useMemo(() => {
    const groups: Record<string, AdminToolCard[]> = {};
    for (const tool of filteredTools) {
      const category = tool.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      // Safe to push after initialization check above
      groups[category].push(tool);
    }
    return groups;
  }, [filteredTools]);

  if (isAdminLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <AdminPageLayout
      title="Admin Hub"
      description="System-wide administrative tools and configurations"
      icon={ShieldAlert}
      isSuperAdminPage={true}
      showBackButton={false}
    >
      <div className="flex flex-col gap-6">
        {/* Search and Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Tabs
            value={selectedCategory}
            onValueChange={setSelectedCategory}
            className="w-full sm:w-auto"
          >
            <TabsList className="grid grid-cols-4 w-full sm:w-auto">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="operations">Daily</TabsTrigger>
              <TabsTrigger value="configuration">Config</TabsTrigger>
              <TabsTrigger value="review">Review</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Tools Grid by Category */}
        {selectedCategory === 'all' ? (
          <div className="space-y-6">
            {Object.entries(categoryInfo).map(([categoryKey, category]) => {
              const tools = groupedTools[categoryKey];
              if (!tools || tools.length === 0) return null;

              return (
                <div key={categoryKey} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <category.icon className={cn('h-5 w-5', category.color)} />
                    <h2 className="text-lg font-semibold">{category.title}</h2>
                    <span className="text-sm text-muted-foreground">- {category.description}</span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {tools.map((tool) => (
                      <Link key={tool.path} to={tool.path} className="block h-full">
                        <Card
                          className={cn(
                            'h-full transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer',
                            'hover:border-primary/50'
                          )}
                        >
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <tool.icon className={cn('h-5 w-5 shrink-0', category.color)} />
                                <span className="text-base">{tool.title}</span>
                              </div>
                            </CardTitle>
                            <CardDescription className="text-sm">
                              {tool.description}
                            </CardDescription>
                          </CardHeader>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredTools.map((tool) => {
              const category = categoryInfo[tool.category];
              return (
                <Link key={tool.path} to={tool.path} className="block h-full">
                  <Card
                    className={cn(
                      'h-full transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer',
                      'hover:border-primary/50'
                    )}
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <tool.icon className={cn('h-5 w-5 shrink-0', category.color)} />
                          <span className="text-base">{tool.title}</span>
                        </div>
                      </CardTitle>
                      <CardDescription className="text-sm">{tool.description}</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        {/* No results message */}
        {filteredTools.length === 0 && (
          <Card className="p-8 text-center">
            <CardContent className="space-y-2">
              <p className="text-muted-foreground">No tools found matching your search.</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
              >
                Clear filters
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminPageLayout>
  );
}
